/**
 * Fit/wrap SVG certificate text. Used by PDF capture (innerHTML never runs <script>)
 * and kept in sync with the inline script in DEFAULT_CERTIFICATE_HTML.
 */
import { applyOrdinalSuffixToFormattedDate } from './certificatePlaceholders';

const SVG_NS = 'http://www.w3.org/2000/svg';

const DATE_ELEMENT_IDS = ['courseDate', 'expiryDate', 'sealExpiryPath'] as const;

function applyCertificateDateOrdinals(root: ParentNode): void {
  for (const id of DATE_ELEMENT_IDS) {
    const el = root.querySelector(`#${id}`);
    if (!el) continue;
    const next = applyOrdinalSuffixToFormattedDate(el.textContent || '');
    if (next && next !== (el.textContent || '').trim()) {
      el.textContent = next;
    }
  }
}

function fitText(element: SVGTextElement): void {
  const limit = Number(element.dataset.fit);
  if (!Number.isFinite(limit) || limit <= 0) return;
  const minimum = Number(element.dataset.minSize || 8);
  let size = Number(element.getAttribute('font-size'));
  if (!Number.isFinite(size)) return;
  while (size > minimum && element.getComputedTextLength() > limit) {
    size -= 0.5;
    element.setAttribute('font-size', String(size));
  }
}

function measureTextWidth(element: SVGTextElement, text: string, fontSize: number): number {
  const parent = element.parentNode;
  if (!parent) return text.length * fontSize * 0.55;

  const probe = document.createElementNS(SVG_NS, 'text');
  probe.setAttribute('font-size', String(fontSize));
  const cls = element.getAttribute('class');
  if (cls) probe.setAttribute('class', cls);
  const weight = element.getAttribute('font-weight');
  if (weight) probe.setAttribute('font-weight', weight);
  const style = element.getAttribute('font-style');
  if (style) probe.setAttribute('font-style', style);
  probe.setAttribute('visibility', 'hidden');
  probe.textContent = text;
  parent.appendChild(probe);
  let width = 0;
  try {
    width = probe.getComputedTextLength();
  } catch {
    width = 0;
  }
  parent.removeChild(probe);

  // Invisible/offscreen mounts sometimes report 0 — fall back to an estimate.
  if (!(width > 0)) {
    width = text.length * fontSize * 0.55;
  }
  return width;
}

function wrapText(element: SVGTextElement): void {
  const limit = Number(element.dataset.wrap);
  if (!Number.isFinite(limit) || limit <= 0) return;

  const maxLines = Number(element.dataset.maxLines || 2);
  const lineHeight = Number(element.dataset.lineHeight || 22);
  const original = element.textContent || '';
  // Preserve intentional newlines (e.g. course title / topic lines), then word-wrap.
  const paragraphs = original.split(/\n/).map((p) => p.trim()).filter(Boolean);
  if (!paragraphs.length) return;

  const cx = Number(element.getAttribute('x'));
  const baseY = Number(element.getAttribute('y'));
  let fontSize = Number(element.getAttribute('font-size'));
  if (!Number.isFinite(fontSize)) fontSize = 16;
  const anchor = element.getAttribute('text-anchor') || 'middle';

  const buildLines = (size: number): string[] => {
    const lines: string[] = [];
    for (const paragraph of paragraphs) {
      const words = paragraph.split(/\s+/).filter(Boolean);
      let current = '';
      for (const word of words) {
        const trial = current ? `${current} ${word}` : word;
        if (measureTextWidth(element, trial, size) <= limit) {
          current = trial;
        } else {
          if (current) lines.push(current);
          current = word;
        }
      }
      if (current) lines.push(current);
    }
    return lines;
  };

  let lines = buildLines(fontSize);
  while (lines.length > maxLines && fontSize > 10) {
    fontSize = Math.max(fontSize - 2, 10);
    element.setAttribute('font-size', String(fontSize));
    lines = buildLines(fontSize);
    if (fontSize <= 10) break;
  }

  element.textContent = '';
  const align = String(element.dataset.wrapAlign || 'center').toLowerCase();
  const startY =
    align === 'top' ? baseY : baseY - (lines.length * lineHeight) / 2 + lineHeight / 2;
  lines.forEach((line, i) => {
    const tspan = document.createElementNS(SVG_NS, 'tspan');
    tspan.setAttribute('x', String(cx));
    tspan.setAttribute('y', String(startY + i * lineHeight));
    tspan.setAttribute('text-anchor', anchor);
    tspan.textContent = line;
    element.appendChild(tspan);
  });
}

/** Apply ordinal dates, then data-fit / data-wrap layout inside a mounted certificate root. */
export function applyCertificateSvgTextLayout(root: ParentNode): void {
  applyCertificateDateOrdinals(root);
  root.querySelectorAll<SVGTextElement>('text[data-fit]').forEach(fitText);
  root.querySelectorAll<SVGTextElement>('text[data-wrap]').forEach(wrapText);
}
